// Swipe Card Stack Component
// Main container managing the card stack and interactions

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SwipeCardStackProps, SwipeDirection } from '../../types/swipe';
import { SwipeCard } from './SwipeCard';
import {
  SwipeCardStackContainer,
  SwipeLoadingContainer,
  SwipeLoadingSpinner,
  SwipeLoadingText,
  SwipeErrorContainer,
  SwipeErrorIcon,
  SwipeErrorText,
  SwipeErrorButton,
  SwipeEmptyContainer,
  SwipeEmptyIcon,
  SwipeEmptyTitle,
  SwipeEmptyText,
  SwipeEmptyButton
} from '../../styles/Swipe/Swipe.styles';
import { logger } from '../../utils/logger';

export const SwipeCardStack: React.FC<SwipeCardStackProps> = ({
  cards,
  currentIndex,
  onSwipe,
  onCardFlip,
  onCardExhausted,
  settings,
  isLoading = false,
  error = null
}) => {
  // Track if we've already called onCardExhausted to prevent infinite loops
  const hasNotifiedExhaustion = useRef(false);

  // Handle card exhaustion - only call once per exhaustion
  useEffect(() => {
    const isExhausted = currentIndex >= cards.length && cards.length > 0;
    
    if (isExhausted && !hasNotifiedExhaustion.current && onCardExhausted) {
      hasNotifiedExhaustion.current = true;
      onCardExhausted();
    }
    
    // Reset flag when new cards are loaded
    if (cards.length > 0 && currentIndex < cards.length) {
      hasNotifiedExhaustion.current = false;
    }
  }, [currentIndex, cards.length, onCardExhausted]);

  // Handle swipe actions (memoized to prevent infinite loops)
  const handleSwipe = useCallback((direction: SwipeDirection) => {
    const currentCard = cards[currentIndex];
    if (currentCard) {
      onSwipe(currentCard.id, direction);
      // Note: Index advancement is now handled by parent SwipeDeck
    }
  }, [cards, currentIndex, onSwipe]);

  // NOTE: Flip functionality moved to external button in SwipeDeck

  // Handle long press (memoized to prevent infinite loops)
  const handleLongPress = useCallback(() => {
    const currentCard = cards[currentIndex];
    if (currentCard) {
      // Long press functionality (e.g., quick preview)
      logger.log('Long press on card:', currentCard.id);
    }
  }, [cards, currentIndex]);

  // Loading state
  if (isLoading) {
    return (
      <SwipeCardStackContainer>
        <SwipeLoadingContainer>
          <SwipeLoadingSpinner />
          <SwipeLoadingText>Loading new cards...</SwipeLoadingText>
        </SwipeLoadingContainer>
      </SwipeCardStackContainer>
    );
  }

  // Error state
  if (error) {
    return (
      <SwipeCardStackContainer>
        <SwipeErrorContainer>
          <SwipeErrorIcon>😔</SwipeErrorIcon>
          <SwipeErrorText>{error}</SwipeErrorText>
          <SwipeErrorButton onClick={() => window.location.reload()}>
            Try Again
          </SwipeErrorButton>
        </SwipeErrorContainer>
      </SwipeCardStackContainer>
    );
  }

  // Empty state
  if (cards.length === 0) {
    return (
      <SwipeCardStackContainer>
        <SwipeEmptyContainer>
          <SwipeEmptyIcon>💔</SwipeEmptyIcon>
          <SwipeEmptyTitle>No more cards</SwipeEmptyTitle>
          <SwipeEmptyText>
            You've seen all available matches. Check back later for new people!
          </SwipeEmptyText>
          <SwipeEmptyButton onClick={() => window.location.reload()}>
            Refresh
          </SwipeEmptyButton>
        </SwipeEmptyContainer>
      </SwipeCardStackContainer>
    );
  }

  // Get visible cards (current + next 2)
  // Show the swiped card during its exit animation, then it will be removed when currentIndex updates
  const visibleCards = cards.slice(currentIndex, currentIndex + 3);

  return (
    <SwipeCardStackContainer>
      <AnimatePresence>
        {visibleCards.map((card, index) => {
          const isTopCard = index === 0;
          const zIndex = 100 - index;
          
          // If this is the top card and it has been swiped, it will animate out
          // The next card will appear after currentIndex updates
          return (
            <SwipeCard
              key={card.id}
              card={card}
              isTopCard={isTopCard}
              stackIndex={index}
              onSwipe={handleSwipe}
              onLongPress={handleLongPress}
              onFlip={onCardFlip ? () => onCardFlip(card.id) : undefined}
              settings={settings}
              zIndex={zIndex}
            />
          );
        })}
      </AnimatePresence>
    </SwipeCardStackContainer>
  );
};
